import { api } from './api';

// Installation als App und Web-Push. Der Service Worker liegt unter /sw.js.

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Läuft Mach's als installierte App (eigenes Fenster, Dock)? */
export function isStandalone(): boolean {
  return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker.register('/sw.js').catch((err) => console.warn('[sw]', err));
}

function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/sw.js'));
}

/** Ist auf diesem Gerät ein Abo aktiv? */
export async function pushActive(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration();
  return !!(await reg?.pushManager.getSubscription());
}

export async function enablePush(): Promise<void> {
  if (!pushSupported()) throw new Error('Dieser Browser kann keine Benachrichtigungen empfangen.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Benachrichtigungen sind im Browser nicht erlaubt.');
  const { key } = await api<{ key: string }>('/push/key');
  const reg = await registration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // Wurde der Server-Schlüssel gewechselt, passt das alte Abo nicht mehr
  if (sub && sub.options.applicationServerKey) {
    const old = new Uint8Array(sub.options.applicationServerKey);
    const now = keyBytes(key);
    if (old.length !== now.length || old.some((b, i) => b !== now[i])) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
  const json = sub.toJSON();
  await api('/push/subscribe', { method: 'POST', json: { endpoint: sub.endpoint, keys: json.keys } });
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api('/push/unsubscribe', { method: 'POST', json: { endpoint: sub.endpoint } }).catch(() => {});
  await sub.unsubscribe();
}

/**
 * Nach der Anmeldung: ein bestehendes Abo dem angemeldeten Konto zuordnen
 * (etwa nach einem Kontowechsel im selben Browser).
 */
export async function refreshPush(): Promise<void> {
  if (!(await pushActive().catch(() => false))) return;
  await enablePush().catch(() => {});
}

/**
 * Beim Abmelden: dieses Gerät bekommt nichts mehr für das Konto. Bewusst ohne
 * `api()`, dessen 401 selbst wieder ein Abmelden auslösen würde.
 */
export async function dropPush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (!sub) return;
    await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-machs-web': '1' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => undefined);
    await sub.unsubscribe();
  } catch {
    // Abmelden geht trotzdem weiter
  }
}

// --- Installation --------------------------------------------------------

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let deferred: InstallPrompt | null = null;
const listeners = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPrompt;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((fn) => fn());
  });
}

export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function canPromptInstall(): boolean {
  return !!deferred;
}

export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  listeners.forEach((fn) => fn());
  return outcome === 'accepted';
}

/** Safari auf dem Mac installiert über das Menü, ohne Aufforderung per Skript. */
export function isSafari(): boolean {
  return /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|Edg|Firefox/.test(navigator.userAgent);
}

// --- Zähler im Tab und am Symbol ----------------------------------------

const BASE_TITLE = "Mach's";

/** Tab-Titel: Zahl ungelesener Nachrichten, die offene Aufgabe, dann der App-Name. */
export function showUnread(n: number, taskTitle?: string | null) {
  const name = taskTitle ? `${taskTitle} | ${BASE_TITLE}` : BASE_TITLE;
  document.title = n > 0 ? `(${n > 99 ? '99+' : n}) ${name}` : name;
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (!nav.setAppBadge) return;
  void (n > 0 ? nav.setAppBadge(n) : nav.clearAppBadge?.())?.catch(() => {});
}
